import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CLICKUP_CUSTOMER_TAG_SOURCE_KIND_LABELS,
  type ClickupCustomerTagSourceKind,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { CheckboxField, SelectField, TextField } from '../../components/ui/Field.js';
import {
  useClickupConfig,
  type ClickupDiscoveryStatus,
  type ClickupLogEntry,
  type ClickupReconcileAuditEntry,
  type ClickupWebhookAuditEntry,
  useClickupDiscovery,
  useClickupLogs,
  useClickupReconcileAudit,
  useClickupReconcileAuditDetail,
  useClickupWebhookAudit,
  useClickupWebhookAuditDetail,
  useClearClickupLogs,
  useBackfillClickupTicketLinks,
  useBackfillClickupReferenceIds,
  useBackfillClickupPsaTicketLinks,
  useClickupReconcile,
  useUpdateClickupReconcile,
  useRunClickupReconcile,
  useClickupWebhookQueue,
  useDrainClickupWebhookQueue,
  useCreateClickupCustomerTagMapping,
  useCreateClickupListMapping,
  useCreateClickupMemberMapping,
  useCreateClickupOpportunityTypeMapping,
  useCreateClickupStatusMapping,
  useDeleteClickupCustomerTagMapping,
  useDeleteClickupListMapping,
  useDeleteClickupMemberMapping,
  useDeleteClickupOpportunityTypeMapping,
  useDeleteClickupStatusMapping,
  useClickupCustomerTagMappings,
  useClickupCustomerTagSources,
  useClickupListMappings,
  useClickupMemberMappings,
  useClickupOpportunityTypeMappings,
  useClickupStatusMappings,
  useMemberOptions,
  useOpportunityTypeOptions,
  useTicketStatusOptions,
  useTicketTypeOptions,
  useUpdateClickupConfig,
} from './api.js';

/** Same normalization the backend uses when matching status mappings to status ids. */
function normalizeMappingKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

function MappedBadge({ mapped }: { mapped: boolean }): JSX.Element {
  const base = 'ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold align-middle';
  return mapped ? (
    <span className={`${base} bg-green-100 text-green-800`}>Mapped</span>
  ) : (
    <span className={`${base} bg-amber-100 text-amber-800`}>Not mapped</span>
  );
}

/** "Connected to" label for a discovered status: its space when space-scoped, else its lists. */
function statusLocationLabel(status: ClickupDiscoveryStatus): string {
  const locations = status.locations ?? [];
  if (locations.length === 0) return '-';
  // Space-scoped status ids start with "p<spaceId>_" and apply to every list in the space.
  if (status.id.startsWith('p')) {
    const spaces = Array.from(new Set(locations.map((entry) => entry.spaceName)));
    return `Space: ${spaces.join(', ')}`;
  }
  return locations.map((entry) => `${entry.listName} (${entry.spaceName})`).join(', ');
}

function MappingTable(props: {
  title: string;
  headers: string[];
  rows: Array<Array<ReactNode>>;
  onDelete?: (rowIndex: number) => void;
  isDeleting?: boolean;
  readOnly?: boolean;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-800">{props.title}</h3>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {props.headers.map((header) => (
                <th key={header} className="px-3 py-2 font-medium">
                  {header}
                </th>
              ))}
              {props.readOnly ? null : <th className="px-3 py-2 font-medium">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {props.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={props.headers.length + (props.readOnly ? 0 : 1)}
                  className="px-3 py-3 text-slate-500"
                >
                  No mappings configured.
                </td>
              </tr>
            ) : (
              props.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-3 py-2 text-slate-700">
                      {cell}
                    </td>
                  ))}
                  {props.readOnly ? null : (
                    <td className="px-3 py-2">
                      <Button
                        size="sm"
                        variant="danger"
                        isLoading={props.isDeleting}
                        onClick={() => props.onDelete?.(rowIndex)}
                      >
                        Delete
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function directionBadge(dir: string): JSX.Element {
  const base = 'inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold';
  if (dir === 'inbound') return <span className={`${base} bg-blue-100 text-blue-800`}>{dir}</span>;
  if (dir === 'outbound')
    return <span className={`${base} bg-violet-100 text-violet-800`}>{dir}</span>;
  return <span className={`${base} bg-slate-100 text-slate-600`}>{dir}</span>;
}

function statusBadge(status: string): JSX.Element {
  const base = 'inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold';
  if (status === 'ok')
    return <span className={`${base} bg-green-100 text-green-800`}>{status}</span>;
  if (status === 'error')
    return <span className={`${base} bg-rose-100 text-rose-800`}>{status}</span>;
  if (status === 'received')
    return <span className={`${base} bg-amber-100 text-amber-800`}>{status}</span>;
  return <span className={`${base} bg-slate-100 text-slate-600`}>{status}</span>;
}

function ClickupLogRow({ entry }: { entry: ClickupLogEntry }): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <div className="flex items-start justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {directionBadge(entry.direction)}
            {statusBadge(entry.status)}
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
              {entry.event}
            </span>
            {entry.taskId ? (
              <span className="font-mono text-xs text-slate-500">task={entry.taskId}</span>
            ) : null}
          </div>
          <p className="mt-1 truncate text-sm text-slate-700" title={entry.detail}>
            {entry.detail}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            {new Date(entry.timestamp).toLocaleString()}
          </p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Hide' : 'Investigate'}
        </Button>
      </div>

      {expanded ? (
        <div className="space-y-3 bg-slate-50 px-4 py-3">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Event</dt>
              <dd className="mt-1 text-sm text-slate-800">{entry.event}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Direction</dt>
              <dd className="mt-1 text-sm text-slate-800">{entry.direction}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Status</dt>
              <dd className="mt-1 text-sm text-slate-800">{entry.status}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Task ID</dt>
              <dd className="mt-1 break-all text-sm text-slate-800">{entry.taskId ?? '—'}</dd>
            </div>
          </dl>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              What happened
            </p>
            <pre className="mt-1 whitespace-pre-wrap break-words rounded-md bg-white p-3 text-xs text-slate-700 ring-1 ring-slate-200">
              {entry.detail}
            </pre>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Next action
            </p>
            <pre className="mt-1 whitespace-pre-wrap break-words rounded-md bg-white p-3 text-xs text-slate-700 ring-1 ring-slate-200">
              {entry.nextAction ?? '—'}
            </pre>
          </div>

          {entry.errorMessage ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Error</p>
              <pre className="mt-1 whitespace-pre-wrap break-words rounded-md bg-white p-3 text-xs text-rose-700 ring-1 ring-slate-200">
                {entry.errorMessage}
              </pre>
            </div>
          ) : null}

          {entry.payloadJson ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Payload
              </p>
              <pre className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md bg-white p-3 text-xs text-slate-700 ring-1 ring-slate-200">
                {entry.payloadJson}
              </pre>
            </div>
          ) : null}

          {entry.resultJson ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Result</p>
              <pre className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md bg-white p-3 text-xs text-slate-700 ring-1 ring-slate-200">
                {entry.resultJson}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function outcomeBadge(outcome: string): JSX.Element {
  const base = 'inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold';
  if (outcome === 'created' || outcome === 'updated' || outcome === 'ok')
    return <span className={`${base} bg-green-100 text-green-800`}>{outcome}</span>;
  if (outcome === 'error' || outcome === 'failed')
    return <span className={`${base} bg-rose-100 text-rose-800`}>{outcome}</span>;
  if (outcome === 'skipped' || outcome === 'ignored')
    return <span className={`${base} bg-amber-100 text-amber-800`}>{outcome}</span>;
  return <span className={`${base} bg-slate-100 text-slate-600`}>{outcome}</span>;
}

function WebhookAuditRow({ entry }: { entry: ClickupWebhookAuditEntry }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // Payload is heavy, so it lives behind the detail endpoint and only loads on expand.
  const detailQuery = useClickupWebhookAuditDetail(expanded ? entry.id : null);

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <div className="flex items-start justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {outcomeBadge(entry.outcome)}
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
              {entry.event}
            </span>
            {entry.taskId ? (
              <span className="font-mono text-xs text-slate-500">task={entry.taskId}</span>
            ) : null}
            {entry.mismatches.length > 0 ? (
              <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                {entry.mismatches.length} mapping mismatch{entry.mismatches.length === 1 ? '' : 'es'}
              </span>
            ) : null}
          </div>
          <p className="mt-1 truncate text-sm text-slate-700" title={entry.summary}>
            {entry.summary}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            {new Date(entry.receivedAt).toLocaleString()} · {entry.durationMs} ms
          </p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Hide' : 'Investigate'}
        </Button>
      </div>

      {expanded ? (
        <div className="space-y-3 bg-slate-50 px-4 py-3">
          {entry.psaTicketId ? (
            <p className="text-xs text-slate-600">
              PSA ticket:{' '}
              <a
                className="font-mono text-brand-700 underline"
                href={`/tickets/${entry.psaTicketId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {entry.psaTicketId}
              </a>
            </p>
          ) : null}

          {entry.mismatches.length > 0 ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                Mapping lookup mismatches
              </p>
              <ul className="mt-1 space-y-1 rounded-md bg-amber-50 p-3 text-xs text-amber-900 ring-1 ring-amber-200">
                {entry.mismatches.map((mismatch, index) => (
                  <li key={index}>
                    <span className="font-semibold uppercase">{mismatch.kind}</span> —{' '}
                    {mismatch.detail}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {entry.errorMessage ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Error</p>
              <pre className="mt-1 whitespace-pre-wrap break-words rounded-md bg-white p-3 text-xs text-rose-700 ring-1 ring-slate-200">
                {entry.errorMessage}
              </pre>
            </div>
          ) : null}

          {entry.steps.length > 0 ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                What it did
              </p>
              <ol className="mt-1 space-y-1 rounded-md bg-white p-3 text-xs text-slate-700 ring-1 ring-slate-200">
                {entry.steps.map((step, index) => (
                  <li key={index} className="flex items-start gap-2">
                    {statusBadge(step.status)}
                    <span className="min-w-0 break-words">{step.detail}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payload</p>
            {detailQuery.isLoading ? (
              <p className="mt-1 text-xs text-slate-500">Loading payload…</p>
            ) : detailQuery.data?.payloadJson ? (
              <pre className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md bg-white p-3 text-xs text-slate-700 ring-1 ring-slate-200">
                {detailQuery.data.payloadJson}
              </pre>
            ) : (
              <p className="mt-1 text-xs text-slate-500">No payload captured.</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ReconcileAuditRow({ entry }: { entry: ClickupReconcileAuditEntry }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // Per-task items live behind the detail endpoint and only load on expand.
  const detailQuery = useClickupReconcileAuditDetail(expanded ? entry.id : null);

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <div className="flex items-start justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {outcomeBadge(entry.outcome)}
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
              {entry.trigger}
            </span>
            <span className="text-xs text-slate-600">
              scanned {entry.scanned} · created {entry.created} · updated {entry.updated} · skipped{' '}
              {entry.skipped} · failed {entry.failed} · {entry.listsScanned} list(s)
            </span>
          </div>
          {entry.message ? <p className="mt-1 text-sm text-slate-700">{entry.message}</p> : null}
          <p className="mt-1 text-xs text-slate-400">
            {new Date(entry.startedAt).toLocaleString()}
            {entry.windowStart
              ? ` · window since ${new Date(entry.windowStart).toLocaleString()}`
              : ''}
          </p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Hide' : `Items (${entry.itemCount})`}
        </Button>
      </div>

      {expanded ? (
        <div className="bg-slate-50 px-4 py-3">
          {detailQuery.isLoading ? (
            <p className="text-xs text-slate-500">Loading items…</p>
          ) : (detailQuery.data?.items ?? []).length === 0 ? (
            <p className="text-xs text-slate-500">
              Nothing was scanned in this run (no tasks changed in the window).
            </p>
          ) : (
            <div className="max-h-96 overflow-auto rounded-lg border border-slate-200 bg-white">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-left uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Task</th>
                    <th className="px-3 py-2 font-medium">List</th>
                    <th className="px-3 py-2 font-medium">Action</th>
                    <th className="px-3 py-2 font-medium">Reason / changes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(detailQuery.data?.items ?? []).map((item, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2 text-slate-700">
                        <span className="font-mono">{item.taskId || '—'}</span>
                        {item.taskName ? (
                          <span className="ml-1 text-slate-500">{item.taskName}</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{item.listName}</td>
                      <td className="px-3 py-2">{outcomeBadge(item.action)}</td>
                      <td className="px-3 py-2 text-slate-600">{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function WebhookAuditCard(): JSX.Element {
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [taskFilter, setTaskFilter] = useState('');
  const auditQuery = useClickupWebhookAudit({});

  const rows = (auditQuery.data ?? []).filter(
    (entry) =>
      (!outcomeFilter || entry.outcome === outcomeFilter) &&
      (!taskFilter.trim() ||
        (entry.taskId ?? '').toLowerCase().includes(taskFilter.trim().toLowerCase())),
  );

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Webhook Audit Log (rolling 45 days)
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              One entry per processed webhook: what it did, errors, and mapping lookup
              mismatches. Entries older than 45 days are purged automatically.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <SelectField
              label="Outcome"
              value={outcomeFilter}
              onChange={(e) => setOutcomeFilter(e.target.value)}
              options={[
                { value: '', label: 'All outcomes' },
                { value: 'created', label: 'Created' },
                { value: 'updated', label: 'Updated' },
                { value: 'unlinked', label: 'Unlinked' },
                { value: 'skipped', label: 'Skipped' },
                { value: 'ignored', label: 'Ignored' },
                { value: 'error', label: 'Error' },
              ]}
            />
            <TextField
              label="Task ID"
              value={taskFilter}
              placeholder="Filter by task…"
              onChange={(e) => setTaskFilter(e.target.value)}
            />
            <Button
              size="sm"
              variant="secondary"
              isLoading={auditQuery.isFetching}
              onClick={() => void auditQuery.refetch()}
            >
              Refresh
            </Button>
          </div>
        </div>

        {auditQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading webhook audit log…</p>
        ) : auditQuery.isError ? (
          <p className="text-sm text-rose-600">Failed to load the webhook audit log.</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500">No webhook audit entries yet.</p>
        ) : (
          <div className="max-h-[36rem] overflow-y-auto rounded-lg border border-slate-200">
            {rows.map((entry) => (
              <WebhookAuditRow key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function ReconcileAuditCard(): JSX.Element {
  const auditQuery = useClickupReconcileAudit();

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Reconcile Audit Log (rolling 45 days)
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              One entry per scheduled or manual reconcile run, with every task scanned and
              whether it was created, updated, skipped, or failed. Entries older than 45 days
              are purged automatically.
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            isLoading={auditQuery.isFetching}
            onClick={() => void auditQuery.refetch()}
          >
            Refresh
          </Button>
        </div>

        {auditQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading reconcile audit log…</p>
        ) : auditQuery.isError ? (
          <p className="text-sm text-rose-600">Failed to load the reconcile audit log.</p>
        ) : (auditQuery.data ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">No reconcile runs recorded yet.</p>
        ) : (
          <div className="max-h-[36rem] overflow-y-auto rounded-lg border border-slate-200">
            {(auditQuery.data ?? []).map((entry) => (
              <ReconcileAuditRow key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export function ClickupPage(): JSX.Element {
  const configQuery = useClickupConfig();
  const listMappingsQuery = useClickupListMappings();
  const memberMappingsQuery = useClickupMemberMappings();
  const statusMappingsQuery = useClickupStatusMappings();
  const opportunityMappingsQuery = useClickupOpportunityTypeMappings();
  const customerTagMappingsQuery = useClickupCustomerTagMappings();
  const customerTagSourcesQuery = useClickupCustomerTagSources();

  const ticketTypesQuery = useTicketTypeOptions();
  const ticketStatusesQuery = useTicketStatusOptions();
  const membersQuery = useMemberOptions();
  const opportunityTypesQuery = useOpportunityTypeOptions();

  const updateConfig = useUpdateClickupConfig();
  const logsQuery = useClickupLogs();
  const clearLogs = useClearClickupLogs();
  const createListMapping = useCreateClickupListMapping();
  const deleteListMapping = useDeleteClickupListMapping();
  const createMemberMapping = useCreateClickupMemberMapping();
  const deleteMemberMapping = useDeleteClickupMemberMapping();
  const createStatusMapping = useCreateClickupStatusMapping();
  const deleteStatusMapping = useDeleteClickupStatusMapping();
  const createOpportunityMapping = useCreateClickupOpportunityTypeMapping();
  const deleteOpportunityMapping = useDeleteClickupOpportunityTypeMapping();
  const createCustomerTagMapping = useCreateClickupCustomerTagMapping();
  const deleteCustomerTagMapping = useDeleteClickupCustomerTagMapping();

  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookId, setWebhookId] = useState('');

  const [listId, setListId] = useState('');
  const [listName, setListName] = useState('');
  const [listTicketTypeId, setListTicketTypeId] = useState('');

  const [memberId, setMemberId] = useState('');
  const [memberName, setMemberName] = useState('');
  const [memberPsaId, setMemberPsaId] = useState('');

  const [statusCode, setStatusCode] = useState('');
  const [statusName, setStatusName] = useState('');
  const [statusTicketStatusId, setStatusTicketStatusId] = useState('');

  const [oppSpaceId, setOppSpaceId] = useState('');
  const [oppFolderId, setOppFolderId] = useState('');
  const [oppTemplateId, setOppTemplateId] = useState('');
  const [opportunityTypeId, setOpportunityTypeId] = useState('');

  // Customer checkbox -> ClickUp tag. The select value packs kind and key as "kind:key"
  // so one control can offer both boolean fields and customer types.
  const [customerTagSource, setCustomerTagSource] = useState('');
  const [customerTagName, setCustomerTagName] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  // Discovery is never triggered automatically — only on explicit button press.
  const [discoveryFired, setDiscoveryFired] = useState(false);

  const discoveryQuery = useClickupDiscovery(selectedWorkspaceId || undefined, discoveryFired);
  const backfillLinks = useBackfillClickupTicketLinks();
  const backfillReferences = useBackfillClickupReferenceIds();
  const backfillPsaLinks = useBackfillClickupPsaTicketLinks();

  const reconcileQuery = useClickupReconcile();
  const updateReconcile = useUpdateClickupReconcile();
  const runReconcile = useRunClickupReconcile();
  const webhookQueue = useClickupWebhookQueue();
  const drainWebhookQueue = useDrainClickupWebhookQueue();

  // Reconcile schedule form state (hydrated from the server config below).
  const [reconcileEnabled, setReconcileEnabled] = useState(false);
  const [reconcileInterval, setReconcileInterval] = useState('');
  const [reconcileLookback, setReconcileLookback] = useState('60');

  // The reference backfill runs in bounded batches server-side (to avoid request
  // timeouts). Loop the batches on the client until nothing remains, accumulating progress.
  const [refBackfill, setRefBackfill] = useState<{
    running: boolean;
    updated: number;
    skipped: number;
    remaining: number | null;
    error: string | null;
    done: boolean;
  }>({ running: false, updated: 0, skipped: 0, remaining: null, error: null, done: false });

  const runReferenceBackfill = async (): Promise<void> => {
    setRefBackfill({
      running: true,
      updated: 0,
      skipped: 0,
      remaining: null,
      error: null,
      done: false,
    });
    let updated = 0;
    let skipped = 0;
    try {
      for (let i = 0; i < 200; i += 1) {
        const result = await backfillReferences.mutateAsync();
        if (result.message) {
          setRefBackfill({
            running: false,
            updated,
            skipped,
            remaining: result.remaining,
            error: result.message,
            done: true,
          });
          return;
        }
        updated += result.updated;
        skipped += result.skipped;
        setRefBackfill({
          running: true,
          updated,
          skipped,
          remaining: result.remaining,
          error: null,
          done: false,
        });
        // Stop when nothing is left, or when a batch made no progress (remaining tickets
        // have no ClickUp custom ID or are persistently erroring).
        if (result.remaining === 0 || result.updated === 0) break;
      }
      setRefBackfill((s) => ({ ...s, running: false, done: true }));
    } catch (err) {
      setRefBackfill((s) => ({
        ...s,
        running: false,
        done: true,
        error:
          err instanceof ApiError
            ? err.message
            : 'Backfill was interrupted. Click again to continue where it left off.',
      }));
    }
  };

  // The PSA Ticket Link backfill also runs in bounded batches server-side; loop until done.
  const [psaLinkBackfill, setPsaLinkBackfill] = useState<{
    running: boolean;
    synced: number;
    failed: number;
    remaining: number | null;
    error: string | null;
    done: boolean;
  }>({ running: false, synced: 0, failed: 0, remaining: null, error: null, done: false });

  const runPsaLinkBackfill = async (): Promise<void> => {
    setPsaLinkBackfill({
      running: true,
      synced: 0,
      failed: 0,
      remaining: null,
      error: null,
      done: false,
    });
    let synced = 0;
    let failed = 0;
    try {
      for (let i = 0; i < 200; i += 1) {
        const result = await backfillPsaLinks.mutateAsync();
        if (result.message) {
          setPsaLinkBackfill({
            running: false,
            synced,
            failed,
            remaining: result.remaining,
            error: result.message,
            done: true,
          });
          return;
        }
        synced += result.synced;
        failed += result.failed;
        setPsaLinkBackfill({
          running: true,
          synced,
          failed,
          remaining: result.remaining,
          error: null,
          done: false,
        });
        // Stop when nothing remains, or when a batch made no progress (persistent failures).
        if (result.remaining === 0 || result.synced === 0) break;
      }
      setPsaLinkBackfill((s) => ({ ...s, running: false, done: true }));
    } catch (err) {
      setPsaLinkBackfill((s) => ({
        ...s,
        running: false,
        done: true,
        error:
          err instanceof ApiError
            ? err.message
            : 'Backfill was interrupted. Click again to continue where it left off.',
      }));
    }
  };

  useEffect(() => {
    const state = reconcileQuery.data;
    if (!state) return;
    setReconcileEnabled(state.enabled);
    setReconcileInterval(state.intervalMinutes != null ? String(state.intervalMinutes) : '');
    setReconcileLookback(String(state.lookbackMinutes));
  }, [reconcileQuery.data]);

  useEffect(() => {
    const config = configQuery.data;
    if (!config) return;
    setEnabled(config.enabled);
    setWebhookId(config.webhookId ?? '');
  }, [configQuery.data]);

  const ticketTypeOptions = useMemo(
    () => (ticketTypesQuery.data ?? []).map((item) => ({ value: item.id, label: item.name })),
    [ticketTypesQuery.data],
  );
  const ticketStatusOptions = useMemo(
    () => (ticketStatusesQuery.data ?? []).map((item) => ({ value: item.id, label: item.name })),
    [ticketStatusesQuery.data],
  );
  const memberOptions = useMemo(
    () =>
      (membersQuery.data ?? [])
        .filter((member) => member.isActive)
        .map((member) => ({ value: member.id, label: member.displayName })),
    [membersQuery.data],
  );
  const opportunityTypeOptions = useMemo(
    () =>
      (opportunityTypesQuery.data ?? []).map((category) => ({
        value: category.id,
        label: category.name,
      })),
    [opportunityTypesQuery.data],
  );
  const customerTagSourceOptions = useMemo(
    () =>
      (customerTagSourcesQuery.data ?? []).map((source) => ({
        value: `${source.kind}:${source.key}`,
        label: `${CLICKUP_CUSTOMER_TAG_SOURCE_KIND_LABELS[source.kind]} — ${source.label}`,
      })),
    [customerTagSourcesQuery.data],
  );

  const discoveryData = discoveryQuery.data?.data;
  const spaceOptions = useMemo(
    () => (discoveryData?.spaces ?? []).map((space) => ({ value: space.id, label: space.name })),
    [discoveryData?.spaces],
  );
  const folderOptions = useMemo(
    () =>
      (discoveryData?.folders ?? [])
        .filter((folder) => !oppSpaceId || folder.spaceId === oppSpaceId)
        .map((folder) => ({ value: folder.id, label: folder.name })),
    [discoveryData?.folders, oppSpaceId],
  );
  const listTemplateOptions = useMemo(
    () =>
      (discoveryData?.templates ?? [])
        .filter((template) => template.type === 'list')
        .map((template) => ({ value: template.id, label: template.name })),
    [discoveryData?.templates],
  );

  const workspaceOptions = useMemo(
    () =>
      (discoveryQuery.data?.workspaces ?? []).map((workspace) => ({
        value: workspace.id,
        label: `${workspace.name} (${workspace.id})`,
      })),
    [discoveryQuery.data?.workspaces],
  );

  // "Mapped" badge lookups: which discovered ids already appear in a mapping table.
  const mappedListIds = useMemo(
    () => new Set((listMappingsQuery.data ?? []).map((mapping) => mapping.clickupListId.trim())),
    [listMappingsQuery.data],
  );
  const mappedMemberIds = useMemo(
    () =>
      new Set((memberMappingsQuery.data ?? []).map((mapping) => mapping.clickupMemberId.trim())),
    [memberMappingsQuery.data],
  );
  const mappedStatusKeys = useMemo(
    () =>
      new Set(
        (statusMappingsQuery.data ?? []).flatMap((mapping) => [
          mapping.clickupStatusCode.trim(),
          normalizeMappingKey(mapping.clickupStatusCode),
        ]),
      ),
    [statusMappingsQuery.data],
  );
  const mappedSpaceIds = useMemo(
    () =>
      new Set(
        (opportunityMappingsQuery.data ?? []).map((mapping) => mapping.clickupSpaceId.trim()),
      ),
    [opportunityMappingsQuery.data],
  );
  const mappedFolderIds = useMemo(
    () =>
      new Set(
        (opportunityMappingsQuery.data ?? [])
          .map((mapping) => mapping.clickupFolderId?.trim())
          .filter((id): id is string => Boolean(id)),
      ),
    [opportunityMappingsQuery.data],
  );
  const mappedTemplateIds = useMemo(
    () =>
      new Set(
        (opportunityMappingsQuery.data ?? [])
          .map((mapping) => mapping.clickupListTemplateId?.trim())
          .filter((id): id is string => Boolean(id)),
      ),
    [opportunityMappingsQuery.data],
  );
  // Status mappings match by status ID (exact or normalized) — never by display name,
  // since identical names in different lists carry different ids.
  const isStatusMapped = (status: ClickupDiscoveryStatus): boolean =>
    mappedStatusKeys.has(status.id) || mappedStatusKeys.has(normalizeMappingKey(status.id));

  useEffect(() => {
    const selected = discoveryQuery.data?.selectedTeamId;
    if (!selected) return;
    setSelectedWorkspaceId(selected);
  }, [discoveryQuery.data?.selectedTeamId]);

  const saveConfigError =
    updateConfig.error instanceof ApiError
      ? updateConfig.error.message
      : updateConfig.error
        ? 'Failed to save ClickUp configuration.'
        : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="ClickUp"
        description="Configure webhook credentials and mapping tables for ClickUp task to PSA ticket synchronization."
      />

      <Card>
        <DataState
          isLoading={configQuery.isLoading}
          isError={configQuery.isError}
          error={configQuery.error}
          onRetry={() => void configQuery.refetch()}
          isEmpty={false}
        >
          <div className="max-w-3xl space-y-4">
            <CheckboxField
              label="Enable ClickUp integration"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <div className="space-y-4">
              <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
                <p className="font-semibold">Getting Started:</p>
                <ul className="mt-2 space-y-1 text-xs">
                  <li>
                    <strong>Personal API Token (required):</strong> Go to ClickUp Settings →
                    Integrations → API tokens. Copy a token starting with{' '}
                    <code className="bg-white px-1 font-mono">pk_</code>
                  </li>
                  <li className="text-blue-800">
                    <em>
                      The Webhook fields below are optional and only needed if you want to receive
                      real-time updates from ClickUp.
                    </em>
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <TextField
                  label="ClickUp Personal API Token (required)"
                  type="password"
                  value={apiKey}
                  placeholder={configQuery.data?.hasApiKey ? 'Stored (enter to replace)' : 'pk_...'}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              </div>
              <div className="space-y-3 rounded-lg bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-900">
                  Webhook Settings (Optional — for future real-time updates)
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    label="Webhook Signing Secret"
                    type="password"
                    value={webhookSecret}
                    placeholder={
                      configQuery.data?.webhookSignatureConfigured
                        ? 'Stored (enter to replace)'
                        : 'Optional'
                    }
                    onChange={(e) => setWebhookSecret(e.target.value)}
                  />
                  <TextField
                    label="ClickUp Webhook ID"
                    value={webhookId}
                    placeholder="Optional"
                    onChange={(e) => setWebhookId(e.target.value)}
                  />
                </div>
              </div>
            </div>
            {saveConfigError ? <p className="text-sm text-rose-600">{saveConfigError}</p> : null}
            <div>
              <Button
                isLoading={updateConfig.isPending}
                onClick={() => {
                  updateConfig.mutate({
                    enabled,
                    ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
                    ...(webhookSecret.trim()
                      ? { webhookSignatureSecret: webhookSecret.trim() }
                      : {}),
                    clickupWebhookId: webhookId.trim() || null,
                  });
                }}
              >
                Save configuration
              </Button>
            </div>
          </div>
        </DataState>
      </Card>

      <Card>
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[20rem] flex-1">
              <SelectField
                label="ClickUp Workspace"
                value={selectedWorkspaceId}
                onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                options={workspaceOptions}
                placeholder="Select workspace"
              />
            </div>
            <Button
              onClick={() => {
                setDiscoveryFired(true);
                void discoveryQuery.refetch();
              }}
              isLoading={discoveryQuery.isFetching}
            >
              Refresh workspace data
            </Button>
          </div>

          <DataState
            isLoading={discoveryQuery.isLoading}
            isError={discoveryQuery.isError}
            error={discoveryQuery.error}
            onRetry={() => void discoveryQuery.refetch()}
            isEmpty={false}
          >
            {!discoveryFired && !discoveryQuery.data ? (
              <p className="py-4 text-sm text-slate-500">
                Press <strong>Refresh workspace data</strong> to load spaces, folders, lists,
                members, statuses, and list templates from ClickUp.
              </p>
            ) : (
              <div className="space-y-6">
                <h2 className="text-base font-semibold text-slate-900">
                  ClickUp Workspace Discovery
                </h2>
                <p className="text-sm text-slate-600">
                  Live metadata from ClickUp to help you map IDs in PSA.
                </p>

                <MappingTable
                  title="ClickUp Spaces (Name and ID)"
                  headers={['Space Name', 'Space ID']}
                  rows={(discoveryQuery.data?.data.spaces ?? []).map((row) => [
                    <span key="name">
                      {row.name}
                      <MappedBadge mapped={mappedSpaceIds.has(row.id)} />
                    </span>,
                    row.id,
                  ])}
                  readOnly
                />

                <MappingTable
                  title="ClickUp Folders (Name and ID)"
                  headers={['Folder Name', 'Folder ID', 'Space']}
                  rows={(discoveryQuery.data?.data.folders ?? []).map((row) => [
                    <span key="name">
                      {row.name}
                      <MappedBadge mapped={mappedFolderIds.has(row.id)} />
                    </span>,
                    row.id,
                    row.spaceName || row.spaceId || '-',
                  ])}
                  readOnly
                />

                <MappingTable
                  title="ClickUp Lists (Name and ID)"
                  headers={['List Name', 'List ID', 'Space']}
                  rows={(discoveryQuery.data?.data.lists ?? []).map((row) => [
                    <span key="name">
                      {row.name}
                      <MappedBadge mapped={mappedListIds.has(row.id)} />
                    </span>,
                    row.id,
                    row.spaceName || row.spaceId || '-',
                  ])}
                  readOnly
                />

                <MappingTable
                  title="ClickUp Members (Name and ID)"
                  headers={['Member Name', 'Member ID', 'Email']}
                  rows={(discoveryQuery.data?.data.members ?? []).map((row) => [
                    <span key="name">
                      {row.name}
                      <MappedBadge mapped={mappedMemberIds.has(row.id)} />
                    </span>,
                    row.id,
                    row.email || '-',
                  ])}
                  readOnly
                />

                <MappingTable
                  title="ClickUp Statuses (Name and ID)"
                  headers={['Status Name', 'Status ID', 'Connected To']}
                  rows={(discoveryQuery.data?.data.statuses ?? []).map((row) => [
                    <span key="name">
                      {row.name}
                      <MappedBadge mapped={isStatusMapped(row)} />
                    </span>,
                    row.id,
                    statusLocationLabel(row),
                  ])}
                  readOnly
                />

                <MappingTable
                  title="ClickUp List Templates (Name and ID)"
                  headers={['Template Name', 'Template ID', 'Type']}
                  rows={(discoveryQuery.data?.data.templates ?? []).map((row) => [
                    <span key="name">
                      {row.name}
                      <MappedBadge mapped={mappedTemplateIds.has(row.id)} />
                    </span>,
                    row.id,
                    row.type,
                  ])}
                  readOnly
                />
              </div>
            )}
          </DataState>
        </div>
      </Card>

      <Card>
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Backfill PSA links to ClickUp
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Go-live prep: pushes each <strong>open</strong> PSA ticket&apos;s public link into its
              linked ClickUp task&apos;s <strong>PSA Ticket Hours</strong> custom field. Only
              non-closed tickets that already have a CU Task ID are processed. New tickets created
              from ClickUp webhooks after go-live sync automatically. Per-task results appear in the
              activity log below.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              isLoading={backfillLinks.isPending}
              onClick={() => backfillLinks.mutate()}
            >
              Backfill ticket links now
            </Button>
            {backfillLinks.isSuccess ? (
              backfillLinks.data.message ? (
                <span className="text-sm text-amber-700">{backfillLinks.data.message}</span>
              ) : (
                <span className="text-sm text-slate-700">
                  {backfillLinks.data.synced} synced, {backfillLinks.data.failed} failed of{' '}
                  {backfillLinks.data.total} eligible ticket(s).
                </span>
              )
            ) : null}
            {backfillLinks.isError ? (
              <span className="text-sm text-rose-600">
                {backfillLinks.error instanceof ApiError
                  ? backfillLinks.error.message
                  : 'Backfill failed.'}
              </span>
            ) : null}
          </div>
        </div>
      </Card>

      <Card>
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Backfill ticket reference IDs
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Pulls each linked ClickUp task&apos;s <strong>Custom Task ID</strong> (e.g.{' '}
              <strong>CST-4021</strong>) into the PSA ticket&apos;s reference, making the custom ID
              the primary identifier everyone sees instead of the numeric ticket number. Runs in
              bounded batches and continues automatically until every ticket is done. New tickets
              created from ClickUp webhooks pick up their custom ID automatically. Per-ticket results
              appear in the activity log below.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button isLoading={refBackfill.running} onClick={() => void runReferenceBackfill()}>
              {refBackfill.running ? 'Backfilling…' : 'Backfill reference IDs now'}
            </Button>
            {refBackfill.error ? (
              <span className="text-sm text-amber-700">{refBackfill.error}</span>
            ) : refBackfill.running ? (
              <span className="text-sm text-slate-700">
                {refBackfill.updated} updated so far
                {refBackfill.remaining !== null ? ` · ${refBackfill.remaining} remaining` : ''}…
              </span>
            ) : refBackfill.done ? (
              <span className="text-sm text-slate-700">
                {refBackfill.updated} updated, {refBackfill.skipped} skipped.
                {refBackfill.remaining && refBackfill.remaining > 0
                  ? ` ${refBackfill.remaining} still without a reference (no ClickUp custom ID or unreachable) — run again to retry.`
                  : ' All tickets processed.'}
              </span>
            ) : null}
          </div>
        </div>
      </Card>

      <Card>
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Backfill PSA Ticket Link to ClickUp
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Pushes each <strong>open</strong> PSA ticket&apos;s <strong>direct ticket URL</strong>{' '}
              (opens the full ticket in PSA) into its linked ClickUp task&apos;s{' '}
              <strong>PSA Ticket Link</strong> custom field. Runs in bounded, resumable batches and
              continues automatically until every ticket is done — already-synced tickets are skipped
              on re-runs. New tickets created from ClickUp webhooks get their PSA Ticket Link
              automatically. Per-ticket results appear in the activity log below.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              isLoading={psaLinkBackfill.running}
              onClick={() => void runPsaLinkBackfill()}
            >
              {psaLinkBackfill.running ? 'Backfilling…' : 'Backfill PSA Ticket Link now'}
            </Button>
            {psaLinkBackfill.error ? (
              <span className="text-sm text-amber-700">{psaLinkBackfill.error}</span>
            ) : psaLinkBackfill.running ? (
              <span className="text-sm text-slate-700">
                {psaLinkBackfill.synced} synced so far
                {psaLinkBackfill.remaining !== null
                  ? ` · ${psaLinkBackfill.remaining} remaining`
                  : ''}
                …
              </span>
            ) : psaLinkBackfill.done ? (
              <span className="text-sm text-slate-700">
                {psaLinkBackfill.synced} synced, {psaLinkBackfill.failed} failed.
                {psaLinkBackfill.remaining && psaLinkBackfill.remaining > 0
                  ? ` ${psaLinkBackfill.remaining} still pending — run again to retry.`
                  : ' All tickets processed.'}
              </span>
            ) : null}
          </div>
        </div>
      </Card>

      {webhookQueue.data &&
      (webhookQueue.data.maintenanceMode ||
        webhookQueue.data.pending > 0 ||
        webhookQueue.data.failed > 0) ? (
        <Card>
          <div className="max-w-3xl space-y-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Queued webhooks</h2>
              <p className="mt-1 text-sm text-slate-600">
                While maintenance mode is on, ClickUp webhooks are still accepted and parked here
                instead of updating tickets. They are processed automatically, oldest first, as
                soon as maintenance mode is turned off.
              </p>
            </div>

            <dl className="grid grid-cols-1 gap-2 rounded-lg bg-slate-50 p-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Maintenance mode</dt>
                <dd className="mt-1 text-slate-800">
                  {webhookQueue.data.maintenanceMode ? 'On — queueing' : 'Off'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Waiting</dt>
                <dd className="mt-1 text-slate-800">
                  {webhookQueue.data.pending}
                  {webhookQueue.data.oldestPendingAt
                    ? ` (oldest ${new Date(webhookQueue.data.oldestPendingAt).toLocaleString()})`
                    : ''}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Failed</dt>
                <dd className="mt-1 text-slate-800">{webhookQueue.data.failed}</dd>
              </div>
            </dl>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                isLoading={drainWebhookQueue.isPending}
                disabled={webhookQueue.data.maintenanceMode || webhookQueue.data.pending === 0}
                onClick={() => drainWebhookQueue.mutate()}
              >
                Process queued webhooks
              </Button>
              {drainWebhookQueue.isSuccess ? (
                <span className="text-sm text-slate-700">
                  {drainWebhookQueue.data.message
                    ? drainWebhookQueue.data.message
                    : `Processed ${drainWebhookQueue.data.processed}, failed ${drainWebhookQueue.data.failed}, ${drainWebhookQueue.data.remaining} still waiting.`}
                </span>
              ) : null}
              {drainWebhookQueue.isError ? (
                <span className="text-sm text-rose-600">
                  {drainWebhookQueue.error instanceof ApiError
                    ? drainWebhookQueue.error.message
                    : 'Processing the queue failed.'}
                </span>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      <Card>
        <DataState
          isLoading={reconcileQuery.isLoading}
          isError={reconcileQuery.isError}
          error={reconcileQuery.error}
          onRetry={() => void reconcileQuery.refetch()}
          isEmpty={false}
        >
          <div className="max-w-3xl space-y-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                Scheduled reconcile (catch missed webhooks)
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Periodically scans ClickUp tasks in every mapped list that changed since the last
                run and upserts the matching PSA ticket — so PSA keeps mirroring ClickUp even when a
                webhook is dropped or fails. Set the cadence, then let the scheduler run it, or run a
                scan on demand. The scheduled scan is paused while maintenance mode is on.
              </p>
            </div>

            <CheckboxField
              label="Enable scheduled reconcile"
              checked={reconcileEnabled}
              onChange={(e) => setReconcileEnabled(e.target.checked)}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                label="Run every (minutes)"
                type="number"
                value={reconcileInterval}
                placeholder="e.g. 15"
                onChange={(e) => setReconcileInterval(e.target.value)}
              />
              <TextField
                label="Lookback overlap (minutes)"
                type="number"
                value={reconcileLookback}
                placeholder="60"
                onChange={(e) => setReconcileLookback(e.target.value)}
              />
            </div>

            <dl className="grid grid-cols-1 gap-2 rounded-lg bg-slate-50 p-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Last run</dt>
                <dd className="mt-1 text-slate-800">
                  {reconcileQuery.data?.lastRunAt
                    ? new Date(reconcileQuery.data.lastRunAt).toLocaleString()
                    : 'Never'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Next run</dt>
                <dd className="mt-1 text-slate-800">
                  {reconcileQuery.data?.nextRunAt
                    ? new Date(reconcileQuery.data.nextRunAt).toLocaleString()
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Scan cursor</dt>
                <dd className="mt-1 text-slate-800">
                  {reconcileQuery.data?.cursor
                    ? new Date(reconcileQuery.data.cursor).toLocaleString()
                    : '—'}
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                isLoading={updateReconcile.isPending}
                onClick={() =>
                  updateReconcile.mutate({
                    enabled: reconcileEnabled,
                    intervalMinutes: reconcileInterval.trim()
                      ? Number(reconcileInterval)
                      : null,
                    lookbackMinutes: reconcileLookback.trim()
                      ? Number(reconcileLookback)
                      : 60,
                  })
                }
              >
                Save schedule
              </Button>
              <Button
                variant="secondary"
                isLoading={runReconcile.isPending}
                onClick={() => runReconcile.mutate()}
              >
                Run reconcile now
              </Button>
              {updateReconcile.isSuccess ? (
                <span className="text-sm text-green-700">Schedule saved.</span>
              ) : null}
              {runReconcile.isSuccess ? (
                <span className="text-sm text-slate-700">
                  {runReconcile.data.message
                    ? runReconcile.data.message
                    : `Scanned ${runReconcile.data.scanned}, created ${runReconcile.data.created}, updated ${runReconcile.data.updated}, skipped ${runReconcile.data.skipped}, failed ${runReconcile.data.failed}.`}
                </span>
              ) : null}
              {runReconcile.isError ? (
                <span className="text-sm text-rose-600">
                  {runReconcile.error instanceof ApiError
                    ? runReconcile.error.message
                    : 'Reconcile failed.'}
                </span>
              ) : null}
            </div>
          </div>
        </DataState>
      </Card>

      <Card>
        <div className="space-y-6">
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-slate-900">Ticket Type by ClickUp List</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <TextField
                label="ClickUp List ID"
                value={listId}
                onChange={(e) => setListId(e.target.value)}
              />
              <TextField
                label="ClickUp List Name"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
              />
              <SelectField
                label="PSA Ticket Type"
                value={listTicketTypeId}
                onChange={(e) => setListTicketTypeId(e.target.value)}
                options={ticketTypeOptions}
                placeholder="Select type"
              />
            </div>
            <Button
              isLoading={createListMapping.isPending}
              onClick={() => {
                if (!listId.trim() || !listName.trim() || !listTicketTypeId) return;
                createListMapping.mutate({
                  clickupListId: listId.trim(),
                  clickupListName: listName.trim(),
                  ticketTypeId: listTicketTypeId,
                });
                setListId('');
                setListName('');
              }}
            >
              Add mapping
            </Button>
            <MappingTable
              title="Configured mappings"
              headers={['ClickUp List ID', 'ClickUp List Name', 'PSA Ticket Type']}
              rows={(listMappingsQuery.data ?? []).map((row) => [
                row.clickupListId,
                row.clickupListName,
                row.ticketType.name,
              ])}
              onDelete={(index) => {
                const row = listMappingsQuery.data?.[index];
                if (!row) return;
                deleteListMapping.mutate(row.id);
              }}
              isDeleting={deleteListMapping.isPending}
            />
          </div>

          <div className="space-y-3">
            <h2 className="text-base font-semibold text-slate-900">Member by ClickUp Assignee</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <TextField
                label="ClickUp Member ID"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
              />
              <TextField
                label="ClickUp Member Name"
                value={memberName}
                onChange={(e) => setMemberName(e.target.value)}
              />
              <SelectField
                label="PSA Member"
                value={memberPsaId}
                onChange={(e) => setMemberPsaId(e.target.value)}
                options={memberOptions}
                placeholder="Select member"
              />
            </div>
            <Button
              isLoading={createMemberMapping.isPending}
              onClick={() => {
                if (!memberId.trim() || !memberName.trim() || !memberPsaId) return;
                createMemberMapping.mutate({
                  clickupMemberId: memberId.trim(),
                  clickupMemberName: memberName.trim(),
                  psaMemberId: memberPsaId,
                });
                setMemberId('');
                setMemberName('');
              }}
            >
              Add mapping
            </Button>
            <MappingTable
              title="Configured mappings"
              headers={['ClickUp Member ID', 'ClickUp Member Name', 'PSA Member']}
              rows={(memberMappingsQuery.data ?? []).map((row) => [
                row.clickupMemberId,
                row.clickupMemberName,
                row.psaMember.displayName,
              ])}
              onDelete={(index) => {
                const row = memberMappingsQuery.data?.[index];
                if (!row) return;
                deleteMemberMapping.mutate(row.id);
              }}
              isDeleting={deleteMemberMapping.isPending}
            />
          </div>

          <div className="space-y-3">
            <h2 className="text-base font-semibold text-slate-900">
              Ticket Status by ClickUp Status
            </h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <TextField
                label="ClickUp Status Code"
                value={statusCode}
                onChange={(e) => setStatusCode(e.target.value)}
              />
              <TextField
                label="ClickUp Status Name"
                value={statusName}
                onChange={(e) => setStatusName(e.target.value)}
              />
              <SelectField
                label="PSA Ticket Status"
                value={statusTicketStatusId}
                onChange={(e) => setStatusTicketStatusId(e.target.value)}
                options={ticketStatusOptions}
                placeholder="Select status"
              />
            </div>
            <Button
              isLoading={createStatusMapping.isPending}
              onClick={() => {
                if (!statusCode.trim() || !statusName.trim() || !statusTicketStatusId) return;
                createStatusMapping.mutate({
                  clickupStatusCode: statusCode.trim(),
                  clickupStatusName: statusName.trim(),
                  ticketStatusId: statusTicketStatusId,
                });
                setStatusCode('');
                setStatusName('');
              }}
            >
              Add mapping
            </Button>
            <MappingTable
              title="Configured mappings"
              headers={['ClickUp Status Code', 'ClickUp Status Name', 'PSA Ticket Status']}
              rows={(statusMappingsQuery.data ?? []).map((row) => [
                row.clickupStatusCode,
                row.clickupStatusName,
                row.ticketStatus.name,
              ])}
              onDelete={(index) => {
                const row = statusMappingsQuery.data?.[index];
                if (!row) return;
                deleteStatusMapping.mutate(row.id);
              }}
              isDeleting={deleteStatusMapping.isPending}
            />
          </div>

          <div className="space-y-3">
            <h2 className="text-base font-semibold text-slate-900">
              Opportunity Type by ClickUp Space, Folder and List Template
            </h2>
            <p className="text-xs text-slate-500">
              Used by the onboarding “Create CU Project” step to create a new ClickUp list (a list
              represents a project) from the chosen list template. Press{' '}
              <strong>Refresh workspace data</strong> above first to load spaces, folders, and list
              templates.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField
                label="ClickUp Space"
                value={oppSpaceId}
                onChange={(e) => {
                  setOppSpaceId(e.target.value);
                  setOppFolderId('');
                }}
                options={spaceOptions}
                placeholder="Select space"
              />
              <SelectField
                label="ClickUp Folder (optional)"
                value={oppFolderId}
                onChange={(e) => setOppFolderId(e.target.value)}
                options={folderOptions}
                placeholder="No folder (create in space)"
              />
              <SelectField
                label="List Template"
                value={oppTemplateId}
                onChange={(e) => setOppTemplateId(e.target.value)}
                options={listTemplateOptions}
                placeholder="Select list template"
              />
              <SelectField
                label="PSA Opportunity Type"
                value={opportunityTypeId}
                onChange={(e) => setOpportunityTypeId(e.target.value)}
                options={opportunityTypeOptions}
                placeholder="Select type"
              />
            </div>
            <Button
              isLoading={createOpportunityMapping.isPending}
              onClick={() => {
                const space = (discoveryData?.spaces ?? []).find((s) => s.id === oppSpaceId);
                const folder = (discoveryData?.folders ?? []).find((f) => f.id === oppFolderId);
                const template = (discoveryData?.templates ?? []).find(
                  (t) => t.id === oppTemplateId,
                );
                if (!space || !opportunityTypeId || !oppTemplateId) return;
                createOpportunityMapping.mutate({
                  clickupSpaceId: space.id,
                  clickupSpaceName: space.name,
                  clickupFolderId: folder?.id ?? null,
                  clickupFolderName: folder?.name ?? null,
                  clickupListTemplateId: template?.id ?? null,
                  clickupListTemplateName: template?.name ?? null,
                  opportunityTypeId,
                });
                setOppSpaceId('');
                setOppFolderId('');
                setOppTemplateId('');
                setOpportunityTypeId('');
              }}
            >
              Add mapping
            </Button>
            <MappingTable
              title="Configured mappings"
              headers={['Space', 'Folder', 'List Template', 'PSA Opportunity Type']}
              rows={(opportunityMappingsQuery.data ?? []).map((row) => [
                row.clickupSpaceName,
                row.clickupFolderName || '—',
                row.clickupListTemplateName || row.clickupListTemplateId || '—',
                row.opportunityType.name,
              ])}
              onDelete={(index) => {
                const row = opportunityMappingsQuery.data?.[index];
                if (!row) return;
                deleteOpportunityMapping.mutate(row.id);
              }}
              isDeleting={deleteOpportunityMapping.isPending}
            />
          </div>

          <div className="space-y-3">
            <h2 className="text-base font-semibold text-slate-900">
              ClickUp Tag by Customer Checkbox
            </h2>
            <p className="text-xs text-slate-500">
              When the webhook (or the scheduled reconcile) creates a PSA ticket, every mapping
              below whose checkbox is ticked on that ticket&rsquo;s customer has its tag added to
              the ClickUp task. Tags are only added, never removed, and{' '}
              <strong>the tag must already exist in the ClickUp Space</strong> — ClickUp tags are
              Space-scoped and PSA does not create them. Both the customer&rsquo;s checkbox fields
              and its Type checkboxes are selectable, and fields added to the customer later show
              up here automatically.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField
                label="Customer checkbox"
                value={customerTagSource}
                onChange={(e) => setCustomerTagSource(e.target.value)}
                options={customerTagSourceOptions}
                placeholder="Select checkbox"
              />
              <TextField
                label="ClickUp tag name"
                value={customerTagName}
                onChange={(e) => setCustomerTagName(e.target.value)}
              />
            </div>
            <Button
              isLoading={createCustomerTagMapping.isPending}
              onClick={() => {
                const separator = customerTagSource.indexOf(':');
                if (separator < 0 || !customerTagName.trim()) return;
                createCustomerTagMapping.mutate({
                  sourceKind: customerTagSource.slice(0, separator) as ClickupCustomerTagSourceKind,
                  sourceKey: customerTagSource.slice(separator + 1),
                  tagName: customerTagName.trim(),
                });
                setCustomerTagSource('');
                setCustomerTagName('');
              }}
            >
              Add mapping
            </Button>
            <MappingTable
              title="Configured mappings"
              headers={['Source', 'Customer checkbox', 'ClickUp tag']}
              rows={(customerTagMappingsQuery.data ?? []).map((row) => [
                CLICKUP_CUSTOMER_TAG_SOURCE_KIND_LABELS[row.sourceKind] ?? row.sourceKind,
                row.sourceLabel,
                row.tagName,
              ])}
              onDelete={(index) => {
                const row = customerTagMappingsQuery.data?.[index];
                if (!row) return;
                deleteCustomerTagMapping.mutate(row.id);
              }}
              isDeleting={deleteCustomerTagMapping.isPending}
            />
          </div>
        </div>
      </Card>

      <WebhookAuditCard />

      <ReconcileAuditCard />

      <Card>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Activity Log</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Inbound webhooks received from ClickUp and outbound API calls made by PSA.
                Auto-refreshes every 15 seconds.
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              isLoading={clearLogs.isPending}
              onClick={() => clearLogs.mutate()}
            >
              Clear log
            </Button>
          </div>

          {logsQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading activity log…</p>
          ) : logsQuery.isError ? (
            <p className="text-sm text-rose-600">Failed to load the activity log.</p>
          ) : (logsQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-slate-500">
              No activity recorded yet. Webhook calls and discovery refreshes will appear here.
            </p>
          ) : (
            <div className="max-h-[36rem] overflow-y-auto rounded-lg border border-slate-200">
              {(logsQuery.data ?? []).map((entry) => (
                <ClickupLogRow key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
